import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit, Trash2, Building2, Eye, MoreVertical } from 'lucide-react';
import { supplierService } from '../services/supplierService';
import { FornecedorResponseDTO, FornecedorSearchParams } from '../types/supplier';
import { PageResponse } from '../types/api';
import { alertService } from '../services/alertService';
import SupplierModal from '../components/suppliers/SupplierModal';

const SuppliersPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<FornecedorResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<FornecedorResponseDTO | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  useEffect(() => {
    loadSuppliers();
  }, [currentPage, searchTerm, activeFilter, stateFilter, cityFilter]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const params: FornecedorSearchParams = {
        page: currentPage,
        size: pageSize,
        sort: 'razaoSocial',
        direction: 'asc',
        filters: {
          ...(searchTerm && { razaoSocial: searchTerm }),
          ...(activeFilter !== undefined && { ativo: activeFilter }),
          ...(stateFilter && { uf: stateFilter }),
          ...(cityFilter && { cidade: cityFilter })
        }
      };

      const response = await supplierService.list(params);
      if (response.success && response.data) {
        setSuppliers(response.data.content);
        setTotalPages(response.data.totalPages);
        setTotalElements(response.data.totalElements);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      alertService.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadSuppliers();
  };

  const handleEdit = (supplier: FornecedorResponseDTO) => {
    setEditingSupplier(supplier);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    const result = await alertService.confirm(
      'Excluir fornecedor',
      'Tem certeza que deseja excluir este fornecedor?',
      'Sim, excluir',
      'Cancelar'
    );
    
    if (result.isConfirmed) {
      try {
        await supplierService.delete(id);
        alertService.success('Fornecedor excluído com sucesso!');
        loadSuppliers();
      } catch (error) {
        console.error('Erro ao excluir fornecedor:', error);
        alertService.error('Erro ao excluir fornecedor');
      }
    }
  };

  const handleToggleActive = async (supplier: FornecedorResponseDTO) => {
    try {
      if (supplier.ativo) {
        await supplierService.deactivate(supplier.id);
        alertService.success('Fornecedor desativado com sucesso!');
      } else {
        await supplierService.activate(supplier.id);
        alertService.success('Fornecedor ativado com sucesso!');
      }
      loadSuppliers();
    } catch (error) {
      console.error('Erro ao alterar status do fornecedor:', error);
      alertService.error('Erro ao alterar status do fornecedor');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingSupplier(null);
    loadSuppliers();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveFilter(undefined);
    setStateFilter('');
    setCityFilter('');
    setCurrentPage(0);
  };

  const formatCnpj = (cnpj: string) => {
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '';
    return phone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Fornecedores</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie os fornecedores da empresa</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span className="sm:inline">Novo Fornecedor</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar fornecedores
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite a razão social..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtros</span>
            </button>
            
            <button
              type="submit"
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Buscar
            </button>
          </div>
        </form>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={activeFilter === undefined ? '' : activeFilter.toString()}
                  onChange={(e) => setActiveFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <input
                  type="text"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  placeholder="Ex: SP, RJ, MG..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade
                </label>
                <input
                  type="text"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  placeholder="Digite a cidade..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {loading ? (
            'Carregando...'
          ) : (
            `Mostrando ${suppliers.length} de ${totalElements} fornecedores`
          )}
        </p>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum fornecedor encontrado</h3>
            <p className="text-gray-600 mb-4">Não há fornecedores cadastrados ou que correspondam aos filtros aplicados.</p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Cadastrar primeiro fornecedor
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fornecedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Localização
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {supplier.razaoSocial}
                          </div>
                          {supplier.nomeFantasia && (
                            <div className="text-sm text-gray-500">
                              {supplier.nomeFantasia}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCnpj(supplier.cnpj)}
                        </div>
                        {supplier.inscricaoEstadual && (
                          <div className="text-sm text-gray-500">
                            IE: {supplier.inscricaoEstadual}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {supplier.email || '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {supplier.telefone ? formatPhone(supplier.telefone) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {supplier.cidade || '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {supplier.uf || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          supplier.ativo
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {supplier.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(supplier)}
                            className={`p-1 rounded ${
                              supplier.ativo
                                ? 'text-orange-600 hover:text-orange-700'
                                : 'text-green-600 hover:text-green-700'
                            }`}
                            title={supplier.ativo ? 'Desativar' : 'Ativar'}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(supplier.id)}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {suppliers.map((supplier) => (
                <div key={supplier.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {supplier.razaoSocial}
                      </h3>
                      {supplier.nomeFantasia && (
                        <p className="text-xs text-gray-500 mb-2">
                          {supplier.nomeFantasia}
                        </p>
                      )}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        supplier.ativo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {supplier.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleEdit(supplier)}
                        className="text-red-600 hover:text-red-700 p-2 rounded"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(supplier)}
                        className={`p-2 rounded ${
                          supplier.ativo
                            ? 'text-orange-600 hover:text-orange-700'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title={supplier.ativo ? 'Desativar' : 'Ativar'}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(supplier.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500 block">CNPJ:</span>
                      <span className="text-gray-900">{formatCnpj(supplier.cnpj)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Localização:</span>
                      <span className="text-gray-900">
                        {supplier.cidade && supplier.uf ? `${supplier.cidade}/${supplier.uf}` : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Email:</span>
                      <span className="text-gray-900 break-all">{supplier.email || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Telefone:</span>
                      <span className="text-gray-900">
                        {supplier.telefone ? formatPhone(supplier.telefone) : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 3, currentPage - 1)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-2 border rounded-lg text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default SuppliersPage;